import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { readableTextOn, seededNumber } from "../../utils/crossroadsTheme";
import "./JourneyScene.css";

/**
 * The Crossroads board — a top-down night city. Players wait in the "Choose your path" panel,
 * two neon roads rise from it, bend around the scenario block and run on up the screen. After
 * each decision the walkers carry on up their road into the next crossroads (stacked above)
 * and the camera follows, so the journey reads as one continuous climb.
 *
 * Two layouts, picked from the board's own shape: LANDSCAPE (1920×1080, desktop reference)
 * and PORTRAIT (828×1792, phone reference). Board art is the admin's background image for that
 * layout when set (its scenario block / start panel / road beds are what the roads are traced
 * on), otherwise a drawn city with the same layout.
 *
 * Parent drives it with `phase`:
 *   idle      players waiting in the start panel (intro / lobby)
 *   approach  walk into the start panel (instant if already there)    → onPhaseDone
 *   decide    waiting for a choice; roads + cards clickable when `interactive`
 *   travel    walk up the chosen road (crowd splits by `split`)        → onPhaseDone
 *   hold      standing on the chosen road (outcome / learning)
 *   merge     carry on up the road to the next crossroads (`next`)    → onPhaseDone
 *   arrive    walk into the destination                               → onPhaseDone
 *   done      standing at the destination
 */

const LANDSCAPE = {
  name: "landscape",
  W: 1920,
  H: 1080,
  pathA:
    "M 820 830 L 820 770 Q 820 690 740 690 L 640 690 Q 555 690 555 605 L 555 355 Q 555 270 640 270 L 700 270 Q 780 270 780 190 L 780 -6",
  pathB:
    "M 1100 830 L 1100 770 Q 1100 690 1180 690 L 1280 690 Q 1365 690 1365 605 L 1365 355 Q 1365 270 1280 270 L 1220 270 Q 1140 270 1140 190 L 1140 -6",
  arrowsA: [[820, 790], [780, 140]],
  arrowsB: [[1100, 790], [1140, 140]],
  panel: { x: 590, y: 825, w: 740, h: 175 },
  home: { y: 905, perRow: 7, gap: 84 },
  scenario: { x: 675, y: 376, w: 570, h: 228 },
  // Text shrinks between these sizes (board units) until it fits its box — see useFitText.
  fit: { option: [26, 13], scenario: [24, 13] },
  cardA: { x: 120, y: 215, w: 380, h: 400 },
  cardB: { x: 1420, y: 215, w: 380, h: 400 },
  pill: { w: 400, h: 60, dy: -28 },
  spot: { from: 600, to: 360, single: 470 },
  // Must stay on screen: option cards down to the start panel.
  focus: { top: 190, bottom: 1005, left: 100, right: 1820 },
  arriveY: 690,
  roadWidth: 90,
};

const PORTRAIT = {
  name: "portrait",
  W: 828,
  H: 1792,
  pathA: "M 296 1300 L 296 1008 Q 296 930 218 930 Q 140 930 140 852 L 140 558 Q 140 480 218 480 Q 296 480 296 402 L 296 -6",
  pathB: "M 532 1300 L 532 1008 Q 532 930 610 930 Q 688 930 688 852 L 688 558 Q 688 480 610 480 Q 532 480 532 402 L 532 -6",
  arrowsA: [[296, 1150], [296, 300]],
  arrowsB: [[532, 1150], [532, 300]],
  panel: { x: 120, y: 1300, w: 588, h: 200 },
  home: { y: 1390, perRow: 7, gap: 78 },
  scenario: { x: 200, y: 588, w: 428, h: 300 },
  fit: { option: [38, 17], scenario: [27, 16] },
  // Kept clear of the roads: ends before road A's edge (x ≈ 249) and above the bend (y ≈ 433);
  // card B mirrors it on the right.
  cardA: { x: 4, y: 118, w: 236, h: 306 },
  cardB: { x: 588, y: 118, w: 236, h: 306 },
  pill: { w: 360, h: 58, dy: -30 },
  spot: { from: 840, to: 580, single: 700 },
  // Must stay on screen: option cards down to the walkers in the start panel.
  focus: { top: 150, bottom: 1450, left: 0, right: 828 },
  arriveY: 940,
  roadWidth: 82,
};

const DURATION = { approach: 1100, travel: 1900, merge: 3000, arrive: 1800 };

/**
 * Live games: how long players' boards take to bring a released crossroads on screen — walking
 * up to the first one, or on from the previous one — so the vote countdown starts only once the
 * options are actually there to choose (a short margin covers devices receiving it late).
 */
export const LIVE_RELEASE_LEAD_MS = {
  first: DURATION.approach + 400,
  next: DURATION.merge + DURATION.approach + 400,
};
const MAX_TOKENS = 14;

const ANIMALS = ["🦁", "🦊", "🐯", "🐻", "🐼", "🐨", "🐱", "🐶", "🐰", "🦝", "🐸", "🐵", "🐹", "🦉", "🐮", "🐷"];
const RINGS = ["#F59E0B", "#EF4444", "#8B5CF6", "#10B981", "#3B82F6", "#EC4899", "#14B8A6", "#F97316"];

/** Stable animal avatar for a player id (same on every device). */
export function avatarFor(id, name = "") {
  const n = seededNumber(String(id ?? name ?? "player"));
  return { id: String(id ?? name), name, emoji: ANIMALS[n % ANIMALS.length], ring: RINGS[(n >>> 4) % RINGS.length] };
}

/**
 * A player's full name laid out to fit under their avatar without running into the
 * neighbours: the font shrinks first, then a long name wraps onto two lines, and only a name
 * that still doesn't fit is squeezed (textLength) — never cut off with "…".
 */
const CHAR_W = 0.56; // average glyph width / font size for the bold UI font
export { fitName, timerPath };
function fitName(name, maxW, base) {
  const full = String(name || "").trim().replace(/\s+/g, " ");
  if (!full) return null;
  const width = (s, size) => s.length * size * CHAR_W;
  if (width(full, base) <= maxW) return { lines: [full], size: base };
  const oneLine = maxW / (full.length * CHAR_W);
  if (oneLine >= base * 0.7) return { lines: [full], size: oneLine };
  // Two lines, split at the space nearest the middle (or mid-word for one long word).
  let cut = -1;
  for (let i = 0; i < full.length; i += 1) {
    if (full[i] === " " && (cut < 0 || Math.abs(i - full.length / 2) < Math.abs(cut - full.length / 2))) cut = i;
  }
  const lines = cut > 0 ? [full.slice(0, cut), full.slice(cut + 1)] : [full.slice(0, Math.ceil(full.length / 2)), full.slice(Math.ceil(full.length / 2))];
  const longest = Math.max(...lines.map((l) => l.length));
  const size = Math.max(base * 0.55, Math.min(base * 0.85, maxW / (longest * CHAR_W)));
  return { lines, size, squeeze: longest * size * CHAR_W > maxW ? maxW : null };
}

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (t) => Math.max(0, Math.min(1, t));

// ---- Drawn city (used when no background image is set) ----------------------------

function Tree({ x, y, r = 34, tone = 0 }) {
  const g = tone % 2 ? ["#1b5e35", "#287d44", "#46a55a"] : ["#1f6b3a", "#2e8b4a", "#4caf5e"];
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r={r} fill={g[0]} />
      <circle cx={-r * 0.35} cy={-r * 0.2} r={r * 0.62} fill={g[1]} />
      <circle cx={r * 0.32} cy={r * 0.12} r={r * 0.58} fill={g[1]} />
      <circle cx={-r * 0.25} cy={-r * 0.35} r={r * 0.3} fill={g[2]} opacity="0.8" />
    </g>
  );
}

function Lamp({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`} className="cr-lamp">
      <circle r="34" fill="url(#cr-lamp-glow)" />
      <circle r="7" fill="#FFE29A" />
    </g>
  );
}

function Planter({ x, y, w, h, trees = [], lamps = [] }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="16" fill="#2a3556" stroke="#3a4870" strokeWidth="4" />
      <rect x={x + 10} y={y + 10} width={w - 20} height={h - 20} rx="10" fill="#1d4d33" />
      {trees.map(([tx, ty, r, tone], i) => (
        <Tree key={i} x={x + tx} y={y + ty} r={r} tone={tone} />
      ))}
      {lamps.map(([lx, ly], i) => (
        <Lamp key={`l${i}`} x={x + lx} y={y + ly} />
      ))}
    </g>
  );
}

function Building({ x, y, w, h }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="10" fill="#2b3658" stroke="#3b4a75" strokeWidth="4" />
      <rect x={x + 22} y={y + 22} width={w - 44} height={h - 60} rx="6" fill="#323f66" />
      <rect x={x + w * 0.2} y={y + h * 0.22} width={w * 0.24} height={h * 0.3} fill="#3c4a77" />
      <polygon points={`${x + w * 0.2},${y + h * 0.22} ${x + w * 0.44},${y + h * 0.52} ${x + w * 0.2},${y + h * 0.52}`} fill="#46568a" />
      {Array.from({ length: Math.floor(w / 70) }, (_, i) => (
        <rect key={i} x={x + 24 + i * 70} y={y + h - 22} width="34" height="10" rx="2" fill="#FFC56B" opacity={(i * 37) % 3 ? 0.9 : 0.25} />
      ))}
    </g>
  );
}

function Crosswalk({ x, y, n }) {
  return (
    <g>
      {Array.from({ length: n }, (_, i) => (
        <rect key={i} x={x + i * 30} y={y} width="16" height="44" rx="2" fill="#e8ecf5" opacity="0.85" />
      ))}
    </g>
  );
}

function BlockAndPanel({ L }) {
  const { scenario: s, panel: p } = L;
  return (
    <>
      <rect x={s.x} y={s.y + 4} width={s.w} height={s.h - 8} rx="22" fill="#151d38" stroke="#3a4870" strokeWidth="4" />
      <rect x={p.x} y={p.y} width={p.w} height={p.h} rx="18" fill="#101833" stroke="#3a4870" strokeWidth="4" />
      <rect x={p.x + 14} y={p.y + 14} width={p.w - 28} height={p.h - 28} rx="12" fill="none" stroke="#2c3a66" strokeWidth="2" />
      <Lamp x={p.x + 8} y={p.y + p.h - 30} />
      <Lamp x={p.x + p.w - 8} y={p.y + p.h - 30} />
    </>
  );
}

function DrawnCityLandscape({ L }) {
  return (
    <g>
      <Building x={-20} y={-20} w={320} h={250} />
      <Building x={L.W - 300} y={-20} w={320} h={250} />
      <Building x={-20} y={760} w={300} h={340} />
      <Building x={L.W - 280} y={760} w={300} h={340} />
      <Planter x={340} y={40} w={170} h={160} trees={[[60, 60, 40, 0], [110, 105, 34, 1]]} lamps={[[150, 140]]} />
      <Planter x={1410} y={40} w={170} h={160} trees={[[60, 60, 40, 1], [112, 104, 34, 0]]} lamps={[[20, 140]]} />
      <Planter x={110} y={600} w={330} h={120} trees={[[60, 60, 38, 0], [150, 64, 34, 1], [260, 58, 40, 0]]} lamps={[[300, 30]]} />
      <Planter x={1480} y={600} w={330} h={120} trees={[[70, 60, 40, 1], [180, 64, 34, 0], [270, 58, 38, 1]]} lamps={[[30, 30]]} />
      <Planter x={880} y={60} w={160} h={170} trees={[[50, 60, 36, 0], [110, 70, 34, 1], [80, 120, 30, 0]]} lamps={[[30, 150], [130, 150]]} />
      <Planter x={490} y={860} w={90} h={190} trees={[[45, 60, 32, 0], [45, 140, 30, 1]]} />
      <Planter x={1340} y={860} w={90} h={190} trees={[[45, 60, 32, 1], [45, 140, 30, 0]]} />
      <Planter x={895} y={725} w={130} h={90} trees={[[38, 45, 30, 0], [92, 45, 30, 1]]} />
      <Crosswalk x={856} y={278} n={7} />
      <Crosswalk x={886} y={666} n={5} />
      <Lamp x={470} y={240} />
      <Lamp x={1450} y={240} />
      <BlockAndPanel L={L} />
    </g>
  );
}

function DrawnCityPortrait({ L }) {
  return (
    <g>
      <Building x={-20} y={1560} w={240} h={260} />
      <Building x={L.W - 220} y={1560} w={240} h={260} />
      <Planter x={360} y={40} w={108} h={360} trees={[[54, 70, 38, 0], [54, 180, 36, 1], [54, 290, 34, 0]]} lamps={[[20, 340], [88, 340]]} />
      <Planter x={-10} y={540} w={60} h={360} trees={[[30, 80, 26, 1], [30, 220, 26, 0]]} />
      <Planter x={L.W - 50} y={540} w={60} h={360} trees={[[30, 80, 26, 0], [30, 220, 26, 1]]} />
      <Planter x={210} y={500} w={130} h={70} trees={[[40, 36, 26, 0], [92, 36, 26, 1]]} lamps={[[12, 60]]} />
      <Planter x={488} y={500} w={130} h={70} trees={[[40, 36, 26, 1], [92, 36, 26, 0]]} lamps={[[118, 60]]} />
      <Planter x={370} y={1040} w={88} h={220} trees={[[44, 60, 32, 0], [44, 160, 30, 1]]} />
      <Planter x={-10} y={1000} w={90} h={260} trees={[[45, 70, 32, 1], [45, 190, 32, 0]]} lamps={[[70, 20]]} />
      <Planter x={L.W - 80} y={1000} w={90} h={260} trees={[[45, 70, 32, 0], [45, 190, 32, 1]]} lamps={[[20, 20]]} />
      <Planter x={240} y={1560} w={348} h={200} trees={[[70, 70, 40, 0], [175, 120, 38, 1], [280, 70, 40, 0]]} />
      <Crosswalk x={352} y={430} n={4} />
      <Crosswalk x={352} y={952} n={4} />
      <BlockAndPanel L={L} />
    </g>
  );
}

/**
 * Board art for one tile: the admin's image, or the drawn city. `mirror` adds flipped copies
 * either side, so a screen wider than the board shows art there instead of empty bars.
 */
function Ground({ L, backgroundUrl, mirror }) {
  if (backgroundUrl) {
    const img = <image href={backgroundUrl} x="0" y="0" width={L.W} height={L.H} preserveAspectRatio="xMidYMid slice" />;
    return (
      <g>
        {mirror ? <g transform="scale(-1 1)">{img}</g> : null}
        {img}
        {mirror ? <g transform={`translate(${2 * L.W} 0) scale(-1 1)`}>{img}</g> : null}
      </g>
    );
  }
  return (
    <g>
      <rect x={mirror ? -L.W : 0} y="0" width={mirror ? 3 * L.W : L.W} height={L.H} fill="#1a2340" />
      <rect x="0" y="0" width={L.W} height={L.H} fill="url(#cr-street-grid)" opacity="0.5" />
      {L.name === "portrait" ? <DrawnCityPortrait L={L} /> : <DrawnCityLandscape L={L} />}
    </g>
  );
}

/** Hover handlers for a road / card pair — mouse only, so a tap never leaves a stuck highlight. */
function hoverProps(onHover, letter, enabled) {
  if (!enabled || !onHover) return {};
  return {
    onPointerEnter: (e) => e.pointerType === "mouse" && onHover(letter),
    onPointerLeave: (e) => e.pointerType === "mouse" && onHover(null),
  };
}

function Road({ d, color, width, dim, glow, interactive, onClick, label, arrows, hover }) {
  return (
    <g
      className={`cr-road${interactive ? " is-interactive" : ""}${dim ? " is-dim" : ""}${glow ? " is-glow" : ""}`}
      {...hover}
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? label : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Soft halo: a wide translucent stroke (no SVG blur filter — far cheaper on phones). */}
      <path d={d} fill="none" stroke={color} strokeWidth={width + 44} strokeLinejoin="round" className="cr-road-glow" />
      <path d={d} fill="none" stroke="#f4f7ff" strokeWidth={width + 12} strokeLinejoin="round" />
      <path d={d} fill="none" stroke={color} strokeWidth={width} strokeLinejoin="round" />
      <path d={d} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="5" strokeDasharray="30 26" />
      {arrows.map(([x, y], i) => (
        <path key={i} d={`M ${x} ${y - 26} L ${x + 18} ${y} L ${x + 7} ${y} L ${x + 7} ${y + 26} L ${x - 7} ${y + 26} L ${x - 7} ${y} L ${x - 18} ${y} Z`} fill="#ffffff" />
      ))}
      {interactive ? <path d={d} fill="none" stroke="transparent" strokeWidth={width + 50} className="cr-road-hit" /> : null}
    </g>
  );
}

// ---- HTML layer ---------------------------------------------------------------------
// Cards, scenario text and labels are plain HTML laid over the SVG (never <foreignObject>:
// iOS Safari mis-positions, clips and flickers HTML inside SVG, especially when it changes).
// Boxes use board units; the layer gets the same scale/offset as the SVG board (fitBoard).

const boxStyle = (b) => ({ left: b.x, top: b.y, width: b.w, height: b.h });

function OptionCard({ box, letter, text, color, interactive, picked, muted, correct, pct, onClick, portrait, hovered, hover, fit }) {
  const hasResult = pct != null || correct;
  const cardRef = useRef(null);
  // The vote % / "Correct path" lines need room too, so the text starts a little smaller then.
  const [fitMax, fitMin] = fit || [31, 15];
  useFitText(cardRef, `${text}|${pct ?? ""}|${correct ? 1 : 0}|${portrait ? "p" : "l"}|${interactive ? 1 : 0}`, hasResult ? Math.round(fitMax * 0.8) : fitMax, fitMin);
  const cls = `cr-opt cr-opt--${letter.toLowerCase()}${picked ? " is-picked" : ""}${muted ? " is-muted" : ""}${
    correct ? " is-correct" : ""
  }${hasResult ? " has-result" : ""}${interactive ? " is-interactive" : ""}${hovered ? " is-hover" : ""}`;
  const content = (
    <>
      {!portrait ? (
        <span className="cr-opt-letter" style={{ background: color, color: readableTextOn(color) }}>
          {letter}
        </span>
      ) : null}
      <span className="cr-opt-text" style={{ color }}>
        {text}
      </span>
      {pct != null ? <span className="cr-opt-pct">{pct}%</span> : null}
      {correct ? <span className="cr-opt-correct">✓ Correct path</span> : null}
    </>
  );
  return (
    <div className="cr-opt-wrap" style={boxStyle(box)}>
      {interactive ? (
        <button ref={cardRef} type="button" className={cls} style={{ "--cr-opt-color": color }} onClick={onClick} aria-label={`Choose road ${letter}: ${text}`} {...hover}>
          {content}
        </button>
      ) : (
        <div ref={cardRef} className={cls} style={{ "--cr-opt-color": color }}>
          {content}
        </div>
      )}
    </div>
  );
}

/**
 * Shrinks the text inside `ref` (via the --fit font size) one step at a time until nothing
 * overflows, from `max` down to `min`. Sizes are board units, and the board scales as a whole,
 * so text that fits here fits on every screen size. Re-runs when the text or layout changes and
 * once web fonts have loaded (they change the text width).
 */
function useFitText(ref, key, max, min) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let cancelled = false;
    const fit = () => {
      if (cancelled) return;
      let size = max;
      el.style.setProperty("--fit", `${size}px`);
      while (size > min && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1)) {
        size -= 1;
        el.style.setProperty("--fit", `${size}px`);
      }
    };
    fit();
    document.fonts?.ready?.then(fit).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [ref, key, max, min]);
}

/** Scenario text, laid into the centre block (pill sits in the block's top slot). */
function ScenarioBlock({ L, label, title, text }) {
  const empty = !label && !title && !text;
  const bodyRef = useRef(null);
  useFitText(bodyRef, `${title || ""}|${text || ""}|${L.name}`, L.fit.scenario[0], L.fit.scenario[1]);
  return (
    <div className="cr-scenario" style={boxStyle(L.scenario)}>
      <span className="cr-scenario-pill">{empty ? "Next crossroads" : label || "Crossroads"}</span>
      <div className="cr-scenario-body" ref={bodyRef}>
        {title ? <strong className="cr-scenario-title">{title}</strong> : null}
        {text ? <p className="cr-scenario-text">{text}</p> : null}
      </div>
    </div>
  );
}

/**
 * The countdown border as one path that starts at the top centre (12 o'clock, under the
 * scenario pill) and runs clockwise back to it — a <rect>'s outline would start at its corner.
 */
function timerPath(w, h, inset = 8, r = 26) {
  const x0 = inset;
  const y0 = inset;
  const x1 = w - inset;
  const y1 = h - inset;
  const cx = w / 2;
  return [
    `M ${cx} ${y0}`,
    `H ${x1 - r}`,
    `A ${r} ${r} 0 0 1 ${x1} ${y0 + r}`,
    `V ${y1 - r}`,
    `A ${r} ${r} 0 0 1 ${x1 - r} ${y1}`,
    `H ${x0 + r}`,
    `A ${r} ${r} 0 0 1 ${x0} ${y1 - r}`,
    `V ${y0 + r}`,
    `A ${r} ${r} 0 0 1 ${x0 + r} ${y0}`,
    `H ${cx}`,
  ].join(" ");
}

/**
 * Voting countdown drawn around the scenario box: the border drains clockwise as time runs out
 * (a CSS animation, so it stays smooth without re-rendering), with the seconds left in a badge.
 */
function VotingTimer({ L, endsAt, total, seconds }) {
  const s = L.scenario;
  const low = seconds <= 5;
  const w = s.w + 20;
  const h = s.h + 12;
  return (
    <div className={`cr-timer${low ? " is-low" : ""}`} style={boxStyle({ x: s.x - 10, y: s.y - 6, w, h })} aria-hidden>
      <svg className="cr-timer-ring" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path className="cr-timer-track" d={timerPath(w, h)} pathLength="100" />
        <TimerBar key={endsAt} endsAt={endsAt} total={total} w={w} h={h} />
      </svg>
      <span className="cr-timer-badge">{seconds}</span>
    </div>
  );
}

/**
 * The draining border. Its start point and length are fixed when this countdown starts (keyed by
 * endsAt), so the per-second re-renders for the badge never restart or speed up the animation.
 */
function TimerBar({ endsAt, total, w, h }) {
  const [timing] = useState(() => {
    const remaining = Math.max(0, endsAt - Date.now());
    const used = total > 0 ? Math.min(100, Math.max(0, 100 - (remaining / total) * 100)) : 100;
    return { remaining, used };
  });
  return (
    <path
      className="cr-timer-bar"
      d={timerPath(w, h)}
      pathLength="100"
      style={{ strokeDashoffset: timing.used, animationDuration: `${timing.remaining}ms` }}
    />
  );
}

const TileHtml = React.memo(function TileHtml({ L, data, current, choosing, selected, reveal, split, onChoose, footerLabel, hovered, onHover, timerEndsAt, timerTotal, timerSeconds }) {
  const colors = data?.pathColors || { a: "#1F6FFF", b: "#FF5A1F" };
  const total = split ? (split.a || 0) + (split.b || 0) : 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  const showPct = current && split != null;
  const hasOptions = Boolean(data?.optionA || data?.optionB);
  const portrait = L.name === "portrait";
  return (
    <>
      {hasOptions ? (
        <>
          <OptionCard
            box={L.cardA}
            letter="A"
            text={data.optionA}
            color={colors.a}
            interactive={current && choosing}
            picked={current && selected === "A"}
            hovered={current && choosing && hovered === "A"}
            hover={hoverProps(onHover, "A", current && choosing)}
            muted={current && (reveal === "B" || (selected === "B" && !split))}
            correct={current && reveal === "A"}
            pct={showPct ? pct(split.a) : null}
            onClick={() => onChoose?.("A")}
            portrait={portrait}
            fit={L.fit.option}
          />
          <OptionCard
            box={L.cardB}
            letter="B"
            text={data.optionB}
            color={colors.b}
            interactive={current && choosing}
            picked={current && selected === "B"}
            hovered={current && choosing && hovered === "B"}
            hover={hoverProps(onHover, "B", current && choosing)}
            muted={current && (reveal === "A" || (selected === "A" && !split))}
            correct={current && reveal === "B"}
            pct={showPct ? pct(split.b) : null}
            onClick={() => onChoose?.("B")}
            portrait={portrait}
            fit={L.fit.option}
          />
        </>
      ) : null}
      <ScenarioBlock L={L} label={data?.label} title={data?.title} text={data?.text} />
      {current && timerEndsAt ? <VotingTimer L={L} endsAt={timerEndsAt} total={timerTotal} seconds={timerSeconds} /> : null}
      {current ? (
        <div className="cr-panel-pill-box" style={{ left: 0, width: L.W, top: L.panel.y + L.pill.dy, height: L.pill.h }}>
          <div className="cr-panel-pill">{footerLabel}</div>
        </div>
      ) : null}
    </>
  );
});

function DestinationHtml({ L, label }) {
  return (
    <div className="cr-scenario cr-scenario--dest" style={boxStyle(L.scenario)}>
      <span className="cr-scenario-pill">You made it</span>
      <div className="cr-scenario-body">
        <span className="cr-dest-flag" aria-hidden>
          🏁
        </span>
        <strong className="cr-dest-label">{label}</strong>
      </div>
    </div>
  );
}

// ---- SVG layer ----------------------------------------------------------------------

/** One crossroads' art: ground and both roads. Memoised — it doesn't change while people walk. */
const TileArt = React.memo(function TileArt({ L, data, backgroundUrl, mirror, current, choosing, selected, reveal, onChoose, hovered, onHover }) {
  const colors = data?.pathColors || { a: "#1F6FFF", b: "#FF5A1F" };
  return (
    <g>
      <Ground L={L} backgroundUrl={backgroundUrl} mirror={mirror} />
      {data ? (
        <>
          <Road
            d={L.pathA}
            color={colors.a}
            width={L.roadWidth}
            interactive={current && choosing}
            dim={current && reveal === "B"}
            glow={current && (reveal === "A" || selected === "A" || (choosing && hovered === "A"))}
            hover={hoverProps(onHover, "A", current && choosing)}
            onClick={() => onChoose?.("A")}
            label={`Choose road A: ${data?.optionA || ""}`}
            arrows={L.arrowsA}
          />
          <Road
            d={L.pathB}
            color={colors.b}
            width={L.roadWidth}
            interactive={current && choosing}
            dim={current && reveal === "A"}
            glow={current && (reveal === "B" || selected === "B" || (choosing && hovered === "B"))}
            hover={hoverProps(onHover, "B", current && choosing)}
            onClick={() => onChoose?.("B")}
            label={`Choose road B: ${data?.optionB || ""}`}
            arrows={L.arrowsB}
          />
        </>
      ) : null}
    </g>
  );
});

/**
 * Where the board sits on screen. It fills the screen like `cover`, but if that would push the
 * layout's focus band (cards → start panel) under the header or a bottom bar, it scales down
 * until the band fits between them; the band is then centred in the clear area. The board art
 * continues above, below and (mirrored) to the sides, so nothing shows as an empty strip.
 */
function fitBoard(L, w, h, insetTop, insetBottom) {
  if (!w || !h) return { scale: 0, offX: 0, offY: 0 };
  const top = Math.min(Math.max(0, insetTop), h * 0.3);
  const bottom = Math.min(Math.max(0, insetBottom), h * 0.3);
  const availH = h - top - bottom;
  const band = L.focus.bottom - L.focus.top;
  let scale = Math.max(w / L.W, h / L.H);
  if (band * scale > availH) scale = availH / band;
  // …and keep both option cards on screen when a squarer screen (e.g. a tablet) crops the sides.
  const bandW = L.focus.right - L.focus.left;
  if (bandW * scale > w) scale = w / bandW;
  const offX = (w - L.W * scale) / 2;
  const centred = top + availH / 2 - (scale * (L.focus.top + L.focus.bottom)) / 2;
  // Prefer the tile covering the whole screen, but move it (only as far as needed) to keep
  // the band clear of the bars.
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const covering = clamp(centred, Math.min(0, h - L.H * scale), Math.max(0, h - L.H * scale));
  const offY = clamp(covering, top - L.focus.top * scale, h - bottom - L.focus.bottom * scale);
  return { scale, offX, offY };
}

/** Layout (portrait when the board is taller than wide) + the board's size in CSS px. */
function useBoardLayout(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w && h) setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [ref]);
  const L = size.w && size.h && size.h > size.w * 1.05 ? PORTRAIT : LANDSCAPE;
  return { L, size };
}

export default function JourneyScene({
  phase = "idle",
  checkpointKey = 0,
  pathColors = { a: "#1F6FFF", b: "#FF5A1F" },
  optionA = "",
  optionB = "",
  scenarioLabel = "",
  scenarioTitle = "",
  scenarioText = "",
  next = null, // { label, title, text, optionA, optionB, pathColors } — the crossroads ahead
  destinationNext = false, // the road ahead leads to the destination
  idleTitle = "",
  idleText = "",
  showText = true,
  selected = null,
  split = null,
  votes = null, // live: { [avatar id]: "A" | "B" } — each walker takes the road they voted for
  avatars = [],
  footerLabel = "Choose your path",
  reveal = null,
  outcome = null,
  interactive = false,
  onChoose,
  onPhaseDone,
  backgroundUrl = "", // landscape art
  backgroundUrlPortrait = "", // portrait art (only used if set — layouts differ)
  destinationLabel = "Destination",
  insetTop = 0, // px covered by the page header (the board still runs underneath it)
  insetBottom = 0, // px covered by bottom bars (HUD / status)
  timerEndsAt = 0, // live voting: when voting closes (local clock ms); 0 = no countdown
  timerTotal = 0, // full voting time in ms (how much of the border is already used up)
  timerSeconds = 0, // whole seconds left, shown in the badge
}) {
  const rootRef = useRef(null);
  const { L, size } = useBoardLayout(rootRef);
  const portrait = L.name === "portrait";
  const art = portrait ? backgroundUrlPortrait : backgroundUrl;
  const CX = L.W / 2;
  const TILE = L.H;

  const pathARef = useRef(null);
  const pathBRef = useRef(null);
  const [geo, setGeo] = useState(null);
  const [tileIndex, setTileIndex] = useState(0);
  const [pan, setPan] = useState(0);
  const [walk, setWalk] = useState({ stage: "offstage", t: 0 });
  const rafRef = useRef(0);
  const doneRef = useRef(onPhaseDone);
  doneRef.current = onPhaseDone;
  const walkRef = useRef(walk);
  walkRef.current = walk;
  const sidesRef = useRef(null);

  useLayoutEffect(() => {
    const measure = (el) => {
      if (!el) return null;
      const total = el.getTotalLength();
      // Roads only ever climb (y decreasing), so binary-search the length at height y.
      const lenAtY = (y) => {
        let lo = 0;
        let hi = total;
        for (let i = 0; i < 30; i += 1) {
          const mid = (lo + hi) / 2;
          if (el.getPointAtLength(mid).y > y) lo = mid;
          else hi = mid;
        }
        return (lo + hi) / 2;
      };
      return { el, total, lenAtY };
    };
    setGeo({ A: measure(pathARef.current), B: measure(pathBRef.current), layout: L.name });
  }, [L]);

  const animate = useCallback((duration, step) => {
    return new Promise((resolve) => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        step(t);
        if (t < 1) rafRef.current = requestAnimationFrame(tick);
        else resolve();
      };
      rafRef.current = requestAnimationFrame(tick);
    });
  }, []);

  // `pan` is a 0–1 fraction of a tile, so a phone rotating mid-walk keeps its place.
  const setPanRatio = useCallback((ratio) => setPan(ratio), []);

  useEffect(() => {
    let cancelled = false;
    const finish = (name) => {
      if (!cancelled) doneRef.current?.(name);
    };
    (async () => {
      switch (phase) {
        case "idle":
        case "decide":
          setPanRatio(0);
          setWalk({ stage: "wait", t: 1 });
          break;
        case "approach":
          setPanRatio(0);
          if (walkRef.current.stage === "wait") {
            // Already in the start panel (we walked here from the previous crossroads).
            finish("approach");
            return;
          }
          await animate(DURATION.approach, (t) => !cancelled && setWalk({ stage: "enter", t: easeInOut(t) }));
          if (cancelled) return;
          setWalk({ stage: "wait", t: 1 });
          finish("approach");
          break;
        case "travel":
          await animate(DURATION.travel, (t) => !cancelled && setWalk({ stage: "travel", t: easeInOut(t) }));
          if (!cancelled) finish("travel");
          break;
        case "hold":
          setWalk({ stage: "travel", t: 1 });
          break;
        case "merge":
          await animate(DURATION.merge, (t) => {
            if (cancelled) return;
            setWalk({ stage: "merge", t });
            // The camera follows the walkers up to the next crossroads.
            setPanRatio(easeInOut(clamp01((t - 0.15) / 0.85)));
          });
          if (cancelled) return;
          setTileIndex((i) => i + 1);
          setPanRatio(0);
          setWalk({ stage: "wait", t: 1 });
          finish("merge");
          break;
        case "arrive":
          setPanRatio(0);
          await animate(DURATION.arrive, (t) => !cancelled && setWalk({ stage: "arrive", t: easeInOut(t) }));
          if (!cancelled) finish("arrive");
          break;
        case "done":
          setPanRatio(0);
          setWalk({ stage: "arrive", t: 1 });
          break;
        default:
          break;
      }
    })();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [phase, checkpointKey, animate, setPanRatio]);

  const isDestination = phase === "arrive" || phase === "done";
  const isCrowd = avatars.length > 1 || split != null;

  // Which road each walker takes — remembered, so walkers stay on their road through
  // hold/merge even after the parent has already moved on to the next question.
  const tokens = useMemo(() => {
    const list = (avatars.length ? avatars : [avatarFor("you")]).slice(0, MAX_TOKENS);
    const total = split ? (split.a || 0) + (split.b || 0) : 0;
    const known = split != null || selected != null;
    const aCount = split
      ? total
        ? Math.round((list.length * (split.a || 0)) / total)
        : Math.round(list.length / 2)
      : selected === "B"
        ? 0
        : list.length;
    // Live crowd with the actual votes: everyone walks the road they chose, and a player who
    // didn't vote (side null) stays in the start panel until the crowd moves on.
    const byVote = split != null && votes != null;
    const sideOf = (a, i) => {
      if (byVote) return votes[a.id] === "A" || votes[a.id] === "B" ? votes[a.id] : null;
      if (known) return split ? (i < aCount ? "A" : "B") : selected;
      const remembered = sidesRef.current;
      return remembered && a.id in remembered ? remembered[a.id] : null;
    };
    const ranks = { A: 0, B: 0, none: 0 };
    const out = list.map((a, i) => {
      const side = sideOf(a, i);
      const rank = ranks[side || "none"]++;
      return { ...a, i, side, rank };
    });
    out.forEach((t) => {
      t.count = Math.max(1, ranks[t.side || "none"]);
    });
    if (known) sidesRef.current = Object.fromEntries(out.map((t) => [t.id, t.side]));
    return out;
  }, [avatars, split, selected, votes]);

  // Each name gets its seat's width in a crowd (so neighbours never overlap), more when alone.
  const nameBase = L.name === "portrait" ? 21 : 19;
  const nameWidth = tokens.length > 1 ? L.home.gap - 8 : 240;
  const names = useMemo(
    () => Object.fromEntries(tokens.map((t) => [t.id, fitName(t.name, nameWidth, nameBase)])),
    [tokens, nameWidth, nameBase]
  );
  const nameFor = (tok) => names[tok.id];

  // Seat in the start panel, centred rows.
  const home = (tok) => {
    const n = tokens.length;
    const perRow = Math.min(L.home.perRow, n);
    const rows = Math.ceil(n / perRow);
    const row = Math.floor(tok.i / perRow);
    const inRow = row === rows - 1 ? n - row * perRow : perRow;
    const col = tok.i % perRow;
    return { x: CX + (col - (inRow - 1) / 2) * L.home.gap, y: L.home.y + (row - (rows - 1) / 2) * L.home.gap };
  };

  const pointOn = (g, len, dx) => {
    const p = g.el.getPointAtLength(Math.max(0, Math.min(g.total, len)));
    return { x: p.x + dx, y: p.y };
  };

  const positionOf = (tok) => {
    const { stage, t } = walk;
    const h = home(tok);
    if (stage === "offstage") return { x: h.x, y: TILE + 120 };
    if (stage === "enter") return { x: h.x, y: lerp(TILE + 120, h.y, t) };
    if (stage === "wait" || !geo || geo.layout !== L.name) return h;
    if (stage === "arrive") return { x: lerp(h.x, CX + (h.x - CX) * 0.8, t), y: lerp(h.y, L.arriveY, t) };
    const dx = isCrowd ? ((tok.rank % 2) * 2 - 1) * 14 : 0;
    if (!tok.side) {
      // Didn't vote: waits in the start panel while the others take their roads, then walks up
      // Path A with the crowd to the next crossroads' start panel.
      if (stage !== "merge") return h;
      const ga = geo.A;
      const start = ga.el.getPointAtLength(0);
      const out = ga.el.getPointAtLength(ga.total);
      const nextHome = { x: h.x, y: h.y - TILE };
      const toRoad = Math.hypot(start.x + dx - h.x, start.y - h.y);
      const across = Math.hypot(nextHome.x - (out.x + dx), nextHome.y - out.y);
      const d = lerp(0, toRoad + ga.total + across, easeInOut(t));
      if (d <= toRoad) {
        const k = toRoad ? d / toRoad : 1;
        return { x: lerp(h.x, start.x + dx, k), y: lerp(h.y, start.y, k) };
      }
      if (d <= toRoad + ga.total) return pointOn(ga, d - toRoad, dx);
      const k = (d - toRoad - ga.total) / across;
      return { x: lerp(out.x + dx, nextHome.x, k), y: lerp(out.y, nextHome.y, k) };
    }
    const g = tok.side === "B" ? geo.B : geo.A;
    const spot = tok.count > 1 ? lerp(g.lenAtY(L.spot.from), g.lenAtY(L.spot.to), tok.rank / (tok.count - 1)) : g.lenAtY(L.spot.single);
    const roadStart = g.el.getPointAtLength(0);
    if (stage === "travel") {
      if (t < 0.22) {
        const k = t / 0.22;
        return { x: lerp(h.x, roadStart.x + dx, k), y: lerp(h.y, roadStart.y, k) };
      }
      return pointOn(g, lerp(0, spot, (t - 0.22) / 0.78), dx);
    }
    // merge: up the rest of the road, off the top, and into the next crossroads' start panel.
    const roadLeft = g.total - spot;
    const exit = g.el.getPointAtLength(g.total);
    const nextHome = { x: h.x, y: h.y - TILE };
    const across = Math.hypot(nextHome.x - (exit.x + dx), nextHome.y - exit.y);
    const d = lerp(0, roadLeft + across, easeInOut(t));
    if (d <= roadLeft) return pointOn(g, spot + d, dx);
    const k = (d - roadLeft) / across;
    return { x: lerp(exit.x + dx, nextHome.x, k), y: lerp(exit.y, nextHome.y, k) };
  };

  const currentData = useMemo(
    () =>
      phase === "idle" && !scenarioText && !scenarioTitle
        ? { label: idleTitle, title: "", text: idleText, pathColors }
        : { label: scenarioLabel, title: scenarioTitle, text: scenarioText, optionA, optionB, pathColors },
    [phase, scenarioText, scenarioTitle, idleTitle, idleText, pathColors, scenarioLabel, optionA, optionB]
  );
  const choosing = interactive && phase === "decide";
  // Hovering a road or its card highlights both (mouse only).
  const [hoverSide, setHoverSide] = useState(null);
  const hovered = choosing ? hoverSide : null;
  const cam = tileIndex * TILE + pan * TILE;
  // Stable click handler, so the memoised tiles don't redraw on every animation frame.
  const chooseRef = useRef(onChoose);
  chooseRef.current = onChoose;
  const choose = useCallback((letter) => chooseRef.current?.(letter), []);

  const { scale, offX, offY } = fitBoard(L, size.w, size.h, insetTop, insetBottom);
  const mirror = offX > 0.5;
  const tileTop = (n) => ({ top: (n - tileIndex) * -TILE });

  return (
    <div ref={rootRef} className={`cr-scene cr-scene--${L.name}`}>
      <svg className="cr-scene-svg" role="img" aria-label="Crossroads road">
        <defs>
          <radialGradient id="cr-lamp-glow">
            <stop offset="0%" stopColor="#FFD27A" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#FFD27A" stopOpacity="0" />
          </radialGradient>
          <pattern id="cr-street-grid" width="120" height="120" patternUnits="userSpaceOnUse">
            <path d="M 120 0 L 0 0 0 120" fill="none" stroke="#243056" strokeWidth="2" />
          </pattern>
        </defs>

        {scale ? (
        <g transform={`translate(${offX} ${offY}) scale(${scale})`}>
        <g transform={`translate(0 ${cam})`}>
          {/* the crossroads we came from (below; seen while the camera moves or on tall screens) */}
          <g transform={`translate(0 ${-(tileIndex - 1) * TILE})`}>
            <TileArt L={L} backgroundUrl={art} mirror={mirror} />
          </g>

          {/* the current crossroads */}
          <g transform={`translate(0 ${-tileIndex * TILE})`}>
            <TileArt
              L={L}
              backgroundUrl={art}
              mirror={mirror}
              data={isDestination ? null : currentData}
              current
              hovered={hovered}
              onHover={setHoverSide}
              choosing={choosing}
              selected={selected}
              reveal={reveal}
              onChoose={choose}
            />
          </g>

          {/* the crossroads ahead (or the destination) */}
          <g transform={`translate(0 ${-(tileIndex + 1) * TILE})`}>
            <TileArt L={L} backgroundUrl={art} mirror={mirror} data={isDestination || destinationNext ? null : next} />
          </g>

          {/* walkers, in current-tile coordinates */}
          <g transform={`translate(0 ${-tileIndex * TILE})`}>
            {tokens.map((tok) => {
              const p = positionOf(tok);
              return (
                <g key={tok.id} transform={`translate(${p.x} ${p.y})`}>
                  <circle r="30" cy="5" fill="rgba(0,0,0,0.35)" />
                  <circle r="29" fill="#ffffff" />
                  <circle r="25" fill="#fff6e6" stroke={tok.ring} strokeWidth="5" />
                  <text textAnchor="middle" dominantBaseline="central" fontSize="30" y="1">
                    {tok.emoji}
                  </text>
                  {nameFor(tok) ? (
                    <text className="cr-token-name" textAnchor="middle" y="50" style={{ fontSize: nameFor(tok).size }}>
                      {nameFor(tok).lines.map((line, li) => (
                        <tspan
                          key={li}
                          x="0"
                          dy={li ? nameFor(tok).size * 1.05 : 0}
                          {...(nameFor(tok).squeeze ? { textLength: nameFor(tok).squeeze, lengthAdjust: "spacingAndGlyphs" } : {})}
                        >
                          {line}
                        </tspan>
                      ))}
                    </text>
                  ) : null}
                </g>
              );
            })}
            {!isCrowd && outcome && phase === "hold"
              ? (() => {
                  const p = positionOf(tokens[0]);
                  const side = p.x > CX ? -1 : 1;
                  return (
                    <g transform={`translate(${p.x + 52 * side} ${p.y - 52})`}>
                      <g className={`cr-fx cr-fx--${outcome}`}>
                        {outcome === "success" ? (
                          <>
                            {[0, 60, 120, 180, 240, 300].map((deg) => (
                              <circle key={deg} className="cr-spark" r="7" fill="#FFD54F" style={{ "--cr-angle": `${deg}deg` }} />
                            ))}
                            <circle r="26" fill="#22C55E" stroke="#fff" strokeWidth="4" />
                            <path d="M -11 1 L -3 9 L 12 -8" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                          </>
                        ) : (
                          <>
                            <circle r="26" fill="#EF4444" stroke="#fff" strokeWidth="4" />
                            <path d="M -9 -9 L 9 9 M 9 -9 L -9 9" stroke="#fff" strokeWidth="6" strokeLinecap="round" />
                          </>
                        )}
                      </g>
                    </g>
                  );
                })()
              : null}
          </g>
        </g>

        {avatars.length > MAX_TOKENS && walk.stage === "wait" && pan === 0 ? (
          <text x={L.panel.x + L.panel.w - 90} y={L.panel.y + L.panel.h - 30} fill="#c7d2fe" fontSize="26" fontWeight="800">
            +{avatars.length - MAX_TOKENS}
          </text>
        ) : null}
        </g>
        ) : null}

        {/* Hidden walking geometry (tile coordinates) */}
        <path ref={pathARef} d={L.pathA} fill="none" stroke="none" />
        <path ref={pathBRef} d={L.pathB} fill="none" stroke="none" />
      </svg>

      {scale ? (
        <div
          className="cr-scene-html"
          style={{ width: L.W, height: L.H, transform: `translate(${offX}px, ${offY}px) scale(${scale}) translateY(${pan * TILE}px)` }}
        >
          <div className="cr-scene-tile" style={tileTop(tileIndex)}>
            {isDestination ? (
              <DestinationHtml L={L} label={destinationLabel} />
            ) : (
              <TileHtml
                L={L}
                data={currentData}
                current
                hovered={hovered}
                onHover={setHoverSide}
                choosing={choosing}
                selected={selected}
                reveal={reveal}
                split={split}
                onChoose={choose}
                footerLabel={footerLabel}
                timerEndsAt={timerEndsAt}
                timerTotal={timerTotal}
                timerSeconds={timerSeconds}
              />
            )}
          </div>
          {!isDestination ? (
            <div className="cr-scene-tile" style={tileTop(tileIndex + 1)}>
              {destinationNext ? <DestinationHtml L={L} label={destinationLabel} /> : <TileHtml L={L} data={next} />}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
